// Main Application Class
import { AccountDialog } from "./components/dialogs/account-dialog";
import { AuthDialog } from "./components/dialogs/auth-dialog";
import { CartDrawer } from "./components/dialogs/cart-drawer";
import { DialogManager } from "./components/dialogs/dialog-manager";
import { OrderConfirmationDialog } from "./components/dialogs/order-confirmation-dialog";
import { ProductDialog } from "./components/dialogs/product-dialog";
import { AboutSection } from "./components/sections/about-section";
import { CategoryBar } from "./components/sections/category-bar";
import { ContactForm } from "./components/sections/contact-form";
import { HeroSection } from "./components/sections/hero-section";
import { MaterialsSection } from "./components/sections/materials-section";
import { ProductGrid } from "./components/sections/product-grid";
import { AuthManager } from "./services/auth-manager";
import { CookieConsentManager } from "./services/cookie-consent-manager";
import { I18nManager } from "./services/i18n-manager";
import { ProductService } from "./services/product-service";
import { ThemeManager } from "./services/theme-manager";
import { CartManager } from "./state/cart-manager";

export class App {
  private dialogManager: DialogManager;
  private accountDialog: AccountDialog;
  private authDialog: AuthDialog;
  private productDialog: ProductDialog;
  private cartDrawer: CartDrawer;
  private orderConfirmationDialog: OrderConfirmationDialog;
  private cartManager: CartManager;
  private authManager: AuthManager;
  private productService: ProductService;
  private i18nManager: I18nManager;
  private themeManager: ThemeManager;
  private cookieConsentManager: CookieConsentManager;
  private contactForm: ContactForm;
  private categoryBar: CategoryBar;
  private productGrid: ProductGrid;
  private heroSection: HeroSection;
  private materialsSection: MaterialsSection;
  private aboutSection: AboutSection;

  constructor() {
    // Initialize services
    this.dialogManager = new DialogManager();
    this.authManager = new AuthManager();
    this.cartManager = new CartManager(this.authManager);
    this.i18nManager = new I18nManager();
    this.productService = new ProductService(this.i18nManager);
    this.themeManager = new ThemeManager();
    this.cookieConsentManager = new CookieConsentManager(
      this.i18nManager,
      this.dialogManager
    );

    // Initialize account dialog after other services
    this.accountDialog = new AccountDialog(
      this.authManager,
      this.i18nManager,
      this.dialogManager
    );

    // Initialize auth dialog
    this.authDialog = new AuthDialog(
      this.authManager,
      this.i18nManager,
      this.dialogManager
    );

    // Initialize product dialog after other services
    this.productDialog = new ProductDialog(
      this.productService,
      this.cartManager,
      this.i18nManager,
      this.dialogManager
    );

    // Initialize cart drawer after other services
    this.cartDrawer = new CartDrawer(
      this.cartManager,
      this.i18nManager,
      this.dialogManager,
      this.authManager,
      this.productService
    );

    // Initialize order confirmation dialog after other services
    this.orderConfirmationDialog = new OrderConfirmationDialog(
      this.i18nManager,
      this.dialogManager,
      this.productService
    );

    // Initialize components
    this.contactForm = new ContactForm(this.i18nManager);
    this.categoryBar = new CategoryBar(this.productService, this.i18nManager);
    this.productGrid = new ProductGrid(
      this.productService,
      this.cartManager,
      this.i18nManager,
      this.dialogManager
    );
    this.heroSection = new HeroSection(this.i18nManager);
    this.materialsSection = new MaterialsSection(this.i18nManager);
    this.aboutSection = new AboutSection(this.i18nManager);
  }

  async init(): Promise<void> {
    try {
      // Initialize internationalization FIRST (before other components)
      // This ensures language is set before components initialize
      await this.i18nManager.init();

      // Initialize theme
      await this.themeManager.init();

      // Initialize authentication (non-blocking on slow networks)
      // Use Promise.race with timeout to prevent blocking on mobile
      const authInitPromise = this.authManager.init();
      const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => {
          // Only warn if auth hasn't initialized yet
          // This prevents false warnings when initialization completes quickly
          if (!this.authManager.isInitialized()) {
            console.warn(
              "Firebase auth initialization taking longer than expected, continuing..."
            );
          }
          resolve(null);
        }, 10000); // 10 second timeout - more reasonable for mobile networks
      });

      // Don't await - let it initialize in background
      Promise.race([authInitPromise, timeoutPromise]).catch((error) => {
        console.error("Auth initialization error (non-blocking):", error);
      });

      // Initialize dialog manager
      this.dialogManager.init();

      // Initialize cookie consent manager (important for GDPR compliance)
      // Use shorter timeout to ensure it shows in production
      if ("requestIdleCallback" in window) {
        requestIdleCallback(
          () => {
            this.cookieConsentManager.init();
          },
          { timeout: 500 }
        );
        // Backup timeout in case requestIdleCallback doesn't fire
        setTimeout(() => {
          this.cookieConsentManager.init();
        }, 1500);
      } else {
        setTimeout(() => {
          this.cookieConsentManager.init();
        }, 100);
      }

      // Initialize cart manager (needed early for cart badge)
      this.cartManager.init();

      // Defer non-critical dialog initialization to improve initial load time
      // These will be initialized lazily when first opened
      // Use requestIdleCallback for better performance, fallback to setTimeout
      const deferInit = (callback: () => void) => {
        if ("requestIdleCallback" in window) {
          requestIdleCallback(callback, { timeout: 2000 });
        } else {
          setTimeout(callback, 100);
        }
      };

      // Initialize critical dialogs immediately (auth needed for login)
      this.authDialog.init();

      // Defer heavy dialogs to improve initial load performance
      deferInit(() => {
        this.accountDialog.init();
        this.productDialog.init();
        this.cartDrawer.init();
        this.orderConfirmationDialog.init();
      });

      // Initialize components
      this.contactForm.init();
      this.categoryBar.init();
      this.productGrid.init();
      this.heroSection.init();
      this.materialsSection.init();
      this.aboutSection.init();

      // Setup event listeners
      this.setupEventListeners();

      // Update cart badge with initial cart state (for guest users or already loaded cart)
      const initialCart = this.cartManager.getCart();
      this.updateCartBadge(initialCart.items.length);

      // Translate hardcoded HTML content (initial translation)
      // Do this after components are initialized so all elements exist
      this.translateStaticContent();

      // Listen for language changes to retranslate static content
      this.i18nManager.onLanguageChange(() => {
        this.translateStaticContent();
      });

      // Load initial data (defer to avoid blocking initial render)
      // Use requestIdleCallback for better performance on mobile
      if ("requestIdleCallback" in window) {
        requestIdleCallback(
          () => {
            this.loadInitialData().catch((error) => {
              console.error("Failed to load initial data:", error);
            });
          },
          { timeout: 1000 }
        );
      } else {
        // Fallback: load after a short delay
        setTimeout(() => {
          this.loadInitialData().catch((error) => {
            console.error("Failed to load initial data:", error);
          });
        }, 100);
      }
    } catch (error) {
      console.error("Failed to initialize app:", error);
      this.showError(
        "Failed to initialize application. Please refresh the page."
      );
    }
  }

  private setupEventListeners(): void {
    // Theme toggle
    const themeToggle = document.getElementById("theme-toggle");
    if (themeToggle) {
      themeToggle.addEventListener("click", () => {
        this.themeManager.toggleTheme();
      });
    }

    // Language toggle
    const languageToggle = document.getElementById("language-toggle");
    if (languageToggle) {
      languageToggle.addEventListener("click", () => {
        this.i18nManager.toggleLanguage();
      });
    }

    // Cart button
    const cartButton = document.getElementById("cart-button");
    if (cartButton) {
      cartButton.addEventListener("click", () => {
        this.dialogManager.openDialog("cart-drawer");
      });
    }

    // Account button
    const accountButton = document.getElementById("account-button");
    if (accountButton) {
      accountButton.addEventListener("click", () => {
        if (this.authManager.isAuthenticated()) {
          this.dialogManager.openDialog("account-dialog");
        } else {
          this.dialogManager.openDialog("auth-dialog");
        }
      });
    }

    // Hero CTA button
    const heroCTA = document.getElementById("hero-cta");
    if (heroCTA) {
      heroCTA.addEventListener("click", () => {
        this.scrollToProducts();
      });
    }

    // Policy links and cookie banner are now handled by CookieConsentManager

    // Keyboard shortcuts
    document.addEventListener("keydown", (e) => {
      this.handleKeyboardShortcuts(e);
    });

    // Handle authentication state changes
    this.authManager.onAuthStateChanged(async (user) => {
      this.updateAccountButton(user);

      // Handle cart sync on auth state changes
      if (user) {
        // User logged in - load cart from server
        // Defer cart loading from server to avoid blocking
        // Load in background after initial render
        if ("requestIdleCallback" in window) {
          requestIdleCallback(
            () => {
              this.cartManager.loadFromServer().catch((error) => {
                console.error("Failed to load cart from server:", error);
              });
            },
            { timeout: 2000 }
          );
        } else {
          setTimeout(() => {
            this.cartManager.loadFromServer().catch((error) => {
              console.error("Failed to load cart from server:", error);
            });
          }, 500);
        }
        // Badge will be updated via onCartUpdated listener when loadFromServer calls notifyListeners
        // But also update immediately in case listener wasn't set up yet
        const cart = this.cartManager.getCart();
        this.updateCartBadge(cart.items.length);
      } else {
        // User logged out - cart stays in localStorage for guest checkout
        // Update badge with current cart state
        const cart = this.cartManager.getCart();
        this.updateCartBadge(cart.items.length);
      }
    });

    // Handle cart updates
    this.cartManager.onCartUpdated((cart) => {
      this.updateCartBadge(cart.items.length);
    });
  }

  private async loadInitialData(): Promise<void> {
    try {
      // Load categories and products
      await Promise.all([
        this.categoryBar.loadCategories(),
        this.productGrid.loadProducts(),
      ]);
    } catch (error) {
      console.error("Failed to load initial data:", error);
      this.showError("Failed to load products. Please refresh the page.");
    }
  }

  private scrollToProducts(): void {
    const productSection = document.querySelector(".product-section");
    if (productSection) {
      productSection.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }

  private handleKeyboardShortcuts(e: KeyboardEvent): void {
    // ESC key closes dialogs
    if (e.key === "Escape") {
      this.dialogManager.closeCurrentDialog();
    }

    // Ctrl/Cmd + K opens search (future feature)
    if ((e.ctrlKey || e.metaKey) && e.key === "k") {
      e.preventDefault();
      // TODO: Implement search
    }
  }

  private updateAccountButton(user: any): void {
    const accountButton = document.getElementById("account-button");
    if (!accountButton) return;

    if (user) {
      accountButton.setAttribute("aria-label", "My Account");
      accountButton.classList.add("authenticated");
    } else {
      accountButton.setAttribute("aria-label", "Sign In");
      accountButton.classList.remove("authenticated");
    }
  }

  private updateCartBadge(count: number): void {
    const cartBadge = document.getElementById("cart-badge");
    if (!cartBadge) return;

    cartBadge.textContent = count.toString();

    if (count > 0) {
      cartBadge.classList.add("show");
      cartBadge.classList.add("animate-bounce");

      // Remove bounce animation after it completes
      setTimeout(() => {
        cartBadge.classList.remove("animate-bounce");
      }, 600);
    } else {
      cartBadge.classList.remove("show");
    }
  }

  private translateStaticContent(): void {
    const currentLang = this.i18nManager.getLanguage();
    const baseUrl = "https://noviprints.com";

    // Update page title
    const titleElement = document.querySelector("title");
    if (titleElement) {
      titleElement.textContent = this.i18nManager.t("page.title");
    }

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        this.i18nManager.t("page.description")
      );
    }

    // Update language meta tag
    const languageMeta = document.querySelector('meta[name="language"]');
    if (languageMeta) {
      languageMeta.setAttribute("content", currentLang);
    }

    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", `${baseUrl}/?lang=${currentLang}`);
    }

    // Update hreflang tags
    this.updateHreflangTags(baseUrl);

    // Update Open Graph title
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute("content", this.i18nManager.t("page.title"));
    }

    // Update Open Graph description
    const ogDescription = document.querySelector(
      'meta[property="og:description"]'
    );
    if (ogDescription) {
      ogDescription.setAttribute(
        "content",
        this.i18nManager.t("page.description")
      );
    }

    // Update Open Graph URL
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute("content", `${baseUrl}/?lang=${currentLang}`);
    }

    // Update Open Graph locale
    const ogLocale = document.querySelector('meta[property="og:locale"]');
    if (ogLocale) {
      ogLocale.setAttribute(
        "content",
        currentLang === "sv" ? "sv_SE" : "en_US"
      );
    }

    // Update Twitter Card title
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute("content", this.i18nManager.t("page.title"));
    }

    // Update Twitter Card description
    const twitterDescription = document.querySelector(
      'meta[name="twitter:description"]'
    );
    if (twitterDescription) {
      twitterDescription.setAttribute(
        "content",
        this.i18nManager.t("page.description")
      );
    }

    // Translate footer content
    const footerCompany = document.querySelector("app-footer h3");
    if (footerCompany) {
      footerCompany.textContent = this.i18nManager.t("footer.company");
    }

    // Footer description - get first paragraph in footer-section
    const footerDescription = document.querySelector(
      "app-footer .footer-section:first-child p"
    );
    if (footerDescription) {
      footerDescription.textContent = this.i18nManager.t("footer.description");
    }

    const footerHeadings = document.querySelectorAll("app-footer h4");
    if (footerHeadings.length > 0) {
      footerHeadings[0].textContent = this.i18nManager.t("footer.quick_links");
    }
    if (footerHeadings.length > 1) {
      footerHeadings[1].textContent = this.i18nManager.t("footer.contact");
    }

    const privacyLink = document.getElementById("privacy-link");
    if (privacyLink) {
      privacyLink.textContent = this.i18nManager.t("footer.privacy_policy");
    }

    const cookieLink = document.getElementById("cookie-link");
    if (cookieLink) {
      cookieLink.textContent = this.i18nManager.t("footer.cookie_policy");
    }

    const termsLink = document.getElementById("terms-link");
    if (termsLink) {
      termsLink.textContent = this.i18nManager.t("footer.terms_of_service");
    }

    const accessibilityLink = document.getElementById("accessibility-link");
    if (accessibilityLink) {
      accessibilityLink.textContent = this.i18nManager.t(
        "footer.accessibility"
      );
    }

    const footerCopyright = document.querySelector(".footer-bottom p");
    if (footerCopyright) {
      footerCopyright.textContent = this.i18nManager.t("footer.copyright");
    }

    // Translate contact section
    const contactTitle = document.querySelector(
      ".contact-section .section-title"
    );
    if (contactTitle) {
      contactTitle.textContent = this.i18nManager.t("contact.subtitle");
    }

    const contactDescription = document.querySelector(".contact-description");
    if (contactDescription) {
      contactDescription.textContent = this.i18nManager.t(
        "contact.description"
      );
    }

    // Translate materials section
    const materialsTitle = document.querySelector(
      ".materials-section .section-title"
    );
    if (materialsTitle) {
      materialsTitle.textContent = this.i18nManager.t("materials.title");
    }

    // Translate category section title
    const categoryTitle = document.getElementById("category-section-title");
    if (categoryTitle) {
      categoryTitle.textContent = this.i18nManager.t("categories.title");
    }

    const materialsDescription = document.querySelector(
      ".materials-section .section-description"
    );
    if (materialsDescription) {
      materialsDescription.textContent = this.i18nManager.t(
        "materials.description"
      );
    }

    // Translate about section
    const aboutTitle = document.querySelector(".about-section .section-title");
    if (aboutTitle) {
      aboutTitle.textContent = this.i18nManager.t("about.title");
    }

    // Translate auth dialog (always translate, don't check content)
    const authDialogTitle = document.querySelector(
      "#auth-dialog .dialog-header h2"
    );
    if (authDialogTitle) {
      authDialogTitle.textContent = this.i18nManager.t("auth.dialog_title");
    }

    const loginTab = document.querySelector(
      "#auth-dialog .auth-tab[data-tab='login']"
    );
    if (loginTab) {
      loginTab.textContent = this.i18nManager.t("auth.login_tab");
    }

    const registerTab = document.querySelector(
      "#auth-dialog .auth-tab[data-tab='register']"
    );
    if (registerTab) {
      registerTab.textContent = this.i18nManager.t("auth.register_tab");
    }

    // Translate cart drawer title
    const cartDrawerTitle = document.getElementById("cart-drawer-title");
    if (cartDrawerTitle) {
      cartDrawerTitle.textContent = this.i18nManager.t("cart.title");
    }
  }

  private updateHreflangTags(baseUrl: string): void {
    // Remove existing hreflang tags
    const existingHreflang = document.querySelectorAll(
      'link[rel="alternate"][hreflang]'
    );
    existingHreflang.forEach((tag) => tag.remove());

    // Create or update hreflang tags
    const head = document.head;

    // Swedish version
    const svHreflang = document.createElement("link");
    svHreflang.setAttribute("rel", "alternate");
    svHreflang.setAttribute("hreflang", "sv");
    svHreflang.setAttribute("href", `${baseUrl}/?lang=sv`);
    head.appendChild(svHreflang);

    // English version
    const enHreflang = document.createElement("link");
    enHreflang.setAttribute("rel", "alternate");
    enHreflang.setAttribute("hreflang", "en");
    enHreflang.setAttribute("href", `${baseUrl}/?lang=en`);
    head.appendChild(enHreflang);

    // Default (Swedish as primary market)
    const defaultHreflang = document.createElement("link");
    defaultHreflang.setAttribute("rel", "alternate");
    defaultHreflang.setAttribute("hreflang", "x-default");
    defaultHreflang.setAttribute("href", `${baseUrl}/?lang=sv`);
    head.appendChild(defaultHreflang);
  }

  private showError(message: string): void {
    // Create error notification
    const notification = document.createElement("div");
    notification.className = "error-notification";
    notification.textContent = message;

    // Add to page
    document.body.appendChild(notification);

    // Animate in
    requestAnimationFrame(() => {
      notification.classList.add("show");
    });

    // Remove after 5 seconds
    setTimeout(() => {
      notification.classList.remove("show");
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 300);
    }, 5000);
  }

  // Public methods for external access
  public getDialogManager(): DialogManager {
    return this.dialogManager;
  }

  public getCartManager(): CartManager {
    return this.cartManager;
  }

  public getAuthManager(): AuthManager {
    return this.authManager;
  }

  public getProductService(): ProductService {
    return this.productService;
  }

  public getI18nManager(): I18nManager {
    return this.i18nManager;
  }

  public getThemeManager(): ThemeManager {
    return this.themeManager;
  }
}
